import { devWarn } from './devLogger'

/**
 * Safe storage access utilities
 * These functions handle SecurityError that occurs in private browsing mode
 * or when third-party cookies are blocked
 */

export const safeSessionStorage = {
  /**
   * Safely get an item from sessionStorage
   * @param key - The storage key
   * @returns The stored value or null if not found or access is denied
   */
  getItem: (key: string): string | null => {
    try {
      if (typeof window === 'undefined') return null
      return sessionStorage.getItem(key)
    } catch (error) {
      devWarn(`Unable to access sessionStorage.getItem("${key}"):`, error)
      return null
    }
  },

  /**
   * Safely set an item in sessionStorage
   * @param key - The storage key
   * @param value - The value to store
   * @returns true if successful, false if access is denied
   */
  setItem: (key: string, value: string): boolean => {
    try {
      if (typeof window === 'undefined') return false
      sessionStorage.setItem(key, value)
      return true
    } catch (error) {
      devWarn(`Unable to access sessionStorage.setItem("${key}"):`, error)
      return false
    }
  },

  /**
   * Safely remove an item from sessionStorage
   * @param key - The storage key
   * @returns true if successful, false if access is denied
   */
  removeItem: (key: string): boolean => {
    try {
      if (typeof window === 'undefined') return false
      sessionStorage.removeItem(key)
      return true
    } catch (error) {
      devWarn(`Unable to access sessionStorage.removeItem("${key}"):`, error)
      return false
    }
  },

  /**
   * Safely clear all items from sessionStorage
   * @returns true if successful, false if access is denied
   */
  clear: (): boolean => {
    try {
      if (typeof window === 'undefined') return false
      sessionStorage.clear()
      return true
    } catch (error) {
      devWarn('Unable to access sessionStorage.clear():', error)
      return false
    }
  },

  /**
   * Safely get and parse a JSON item from sessionStorage
   * @param key - The storage key
   * @param defaultValue - The default value to return if parsing fails or item doesn't exist
   * @returns The parsed value or defaultValue
   */
  getJSON: <T>(key: string, defaultValue: T): T => {
    const value = safeSessionStorage.getItem(key)
    if (!value) return defaultValue
    try {
      return JSON.parse(value) as T
    } catch (error) {
      devWarn(`Failed to parse JSON from sessionStorage["${key}"]:`, error)
      return defaultValue
    }
  },

  /**
   * Safely stringify and set a JSON item in sessionStorage
   * @param key - The storage key
   * @param value - The value to store (will be JSON.stringified)
   * @returns true if successful, false if access is denied or JSON.stringify fails
   */
  setJSON: <T>(key: string, value: T): boolean => {
    try {
      const jsonString = JSON.stringify(value)
      return safeSessionStorage.setItem(key, jsonString)
    } catch (error) {
      devWarn(`Failed to stringify JSON for sessionStorage["${key}"]:`, error)
      return false
    }
  },
}

export const safeLocalStorage = {
  /**
   * Safely get an item from localStorage
   * @param key - The storage key
   * @returns The stored value or null if not found or access is denied
   */
  getItem: (key: string): string | null => {
    try {
      if (typeof window === 'undefined') return null
      return localStorage.getItem(key)
    } catch (error) {
      devWarn(`Unable to access localStorage.getItem("${key}"):`, error)
      return null
    }
  },

  /**
   * Safely set an item in localStorage
   * @param key - The storage key
   * @param value - The value to store
   * @returns true if successful, false if access is denied
   */
  setItem: (key: string, value: string): boolean => {
    try {
      if (typeof window === 'undefined') return false
      localStorage.setItem(key, value)
      return true
    } catch (error) {
      devWarn(`Unable to access localStorage.setItem("${key}"):`, error)
      return false
    }
  },

  /**
   * Safely remove an item from localStorage
   * @param key - The storage key
   * @returns true if successful, false if access is denied
   */
  removeItem: (key: string): boolean => {
    try {
      if (typeof window === 'undefined') return false
      localStorage.removeItem(key)
      return true
    } catch (error) {
      devWarn(`Unable to access localStorage.removeItem("${key}"):`, error)
      return false
    }
  },

  /**
   * Safely clear all items from localStorage
   * @returns true if successful, false if access is denied
   */
  clear: (): boolean => {
    try {
      if (typeof window === 'undefined') return false
      localStorage.clear()
      return true
    } catch (error) {
      devWarn('Unable to access localStorage.clear():', error)
      return false
    }
  },

  /**
   * Safely get and parse a JSON item from localStorage
   * @param key - The storage key
   * @param defaultValue - The default value to return if parsing fails or item doesn't exist
   * @returns The parsed value or defaultValue
   */
  getJSON: <T>(key: string, defaultValue: T): T => {
    const value = safeLocalStorage.getItem(key)
    if (!value) return defaultValue
    try {
      return JSON.parse(value) as T
    } catch (error) {
      devWarn(`Failed to parse JSON from localStorage["${key}"]:`, error)
      return defaultValue
    }
  },

  /**
   * Safely stringify and set a JSON item in localStorage
   * @param key - The storage key
   * @param value - The value to store (will be JSON.stringified)
   * @returns true if successful, false if access is denied or JSON.stringify fails
   */
  setJSON: <T>(key: string, value: T): boolean => {
    try {
      const jsonString = JSON.stringify(value)
      return safeLocalStorage.setItem(key, jsonString)
    } catch (error) {
      devWarn(`Failed to stringify JSON for localStorage["${key}"]:`, error)
      return false
    }
  },
}
